import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";
import { History, RotateCcw, ChevronDown, ChevronUp, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function VersionTimeline({ promptId, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (promptId) loadVersions();
  }, [promptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVersions = async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/prompts/${promptId}/versions`);
      setVersions(data.versions || []);
      setCurrentVersion(data.current_version || 0);
    } catch (e) {
      // No versions yet — that's fine
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (versionNum) => {
    setRestoring(versionNum);
    try {
      const { data } = await API.post(`/prompts/${promptId}/restore-version`, { version_num: versionNum });
      toast.success(`Restored to v${versionNum}`, { description: "Your current state was saved as a new version before restoring." });
      if (onRestored) onRestored(data);
      loadVersions();
    } catch (e) {
      toast.error("Restore failed", { description: e.response?.data?.detail || "Try again." });
    } finally {
      setRestoring(null);
    }
  };

  if (loading && versions.length === 0) return null;
  if (versions.length === 0) return null;

  return (
    <div className="border border-[var(--pf-border)] bg-[var(--pf-bg)]" data-testid="version-timeline">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[var(--pf-surface)] transition-colors duration-150"
        data-testid="version-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="w-3.5 h-3.5 text-[var(--pf-accent)]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)]">Versions</span>
          <span className="text-[10px] px-1.5 py-0.5 bg-[var(--pf-accent-subtle)] text-[var(--pf-accent)] font-medium">{versions.length}</span>
        </div>
        {expanded ? <ChevronUp className="w-3 h-3 text-[var(--pf-text-muted)]" /> : <ChevronDown className="w-3 h-3 text-[var(--pf-text-muted)]" />}
      </button>

      {expanded && (
        <div className="border-t border-[var(--pf-border)] max-h-[240px] overflow-y-auto">
          {/* Current state indicator */}
          <div className="px-4 py-2 bg-[var(--pf-accent-subtle)] border-b border-[var(--pf-border)] flex items-center gap-2">
            <div className="w-2 h-2 bg-[var(--pf-accent)] shrink-0" />
            <span className="text-[11px] font-medium text-[var(--pf-accent)]">Current state</span>
            <span className="text-[10px] text-[var(--pf-text-muted)] ml-auto">v{currentVersion + 1}</span>
          </div>

          {versions.map((v) => (
            <div
              key={v.version_num}
              className="px-4 py-2.5 border-b border-[var(--pf-border)] last:border-b-0 hover:bg-[var(--pf-surface)] transition-colors group"
              data-testid={`version-${v.version_num}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-2 h-2 bg-[var(--pf-border)] shrink-0" />
                  <span className="text-[11px] font-medium text-[var(--pf-text)]">v{v.version_num}</span>
                  <span className="text-[10px] text-[var(--pf-text-muted)] truncate">{v.trigger}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => handleRestore(v.version_num)}
                    disabled={restoring === v.version_num}
                    data-testid={`restore-${v.version_num}`}
                    className="h-6 px-2 rounded-none text-[10px] text-[var(--pf-accent)] hover:bg-[var(--pf-accent)] hover:text-white"
                  >
                    {restoring === v.version_num ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RotateCcw className="w-2.5 h-2.5 mr-1" />Restore</>}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1 ml-4">
                <Clock className="w-2.5 h-2.5 text-[var(--pf-text-muted)]" />
                <span className="text-[10px] text-[var(--pf-text-muted)]">
                  {new Date(v.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
                {v.master_prompt && <span className="text-[10px] text-[var(--pf-text-muted)]">master prompt</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
